import useCommonApi from "../api/useCommonApi";
import { aplicarCargosAutomaticosModalidadGuia } from "../components/servicioAdicional/aplicarCargosAutomaticosModalidadGuiaHelpers";
import { IRecoleccion } from "../redux/types/recoleccionActionTypes";
import { ServicioAdicional, IServicioAdicional } from "../entities/catalogos/ServicioAdicional";
import { ICliente } from "../entities/catalogos/ICliente";
import { enums } from "../enums/enums";

const CONCEPTOS_CITA_CEDIS_EXCLUIDOS_GUIA = new Set<number>([49, 50]);

const useGetServiciosGuia = () => {
    const apiSource = useCommonApi();

    const esServicioCatalogoE3 = (s: IServicioAdicional | ServicioAdicional): boolean =>
        s?.configuracion?.esDocumentacionGuia === true
        && s?.configuracion?.esSolicitudServicioSeccionServiciosAdicionales === true;

    const filtrarCobroEnDestinoClienteCredito = (
        servicios: ServicioAdicional[],
        cliente: ICliente
    ): ServicioAdicional[] => {
        const clienteCredito = cliente?.clienteCredito;
        if (clienteCredito && clienteCredito.tipoPagoID === enums.tipoPago.credito) {
            return servicios.filter(o => o.nombre !== "COBRO EN DESTINO");
        }
        return servicios;
    };

    const excluirCargosCitaCedisGuiaInHouse = (servicios: ServicioAdicional[]): ServicioAdicional[] =>
        servicios.filter(s => !CONCEPTOS_CITA_CEDIS_EXCLUIDOS_GUIA.has(s.id));

    const combinarServiciosGuia = (
        recoleccionOrigen: IRecoleccion,
        recoleccionDestino: IRecoleccion
    ): ServicioAdicional[] => {
        const serviciosOrigen = (recoleccionOrigen.areaServicio?.serviciosAdicionales ?? [])
            .filter(esServicioCatalogoE3);
        const serviciosDestino = (recoleccionDestino.areaServicio?.serviciosAdicionales ?? [])
            .filter(esServicioCatalogoE3);

        const combinados: ServicioAdicional[] = [];

        serviciosOrigen.forEach(s => {
            const nuevo = new ServicioAdicional(s);
            const tambienEnDestino = serviciosDestino.some(sd => sd.id === s.id);
            configurarParaPasoServiciosGuia(nuevo, true, tambienEnDestino);
            combinados.push(nuevo);
        });

        serviciosDestino.forEach(s => {
            if (combinados.some(e => e.id === s.id)) {
                return;
            }
            const nuevo = new ServicioAdicional(s);
            configurarParaPasoServiciosGuia(nuevo, false, true);
            combinados.push(nuevo);
        });

        return combinados;
    };

    const configurarParaPasoServiciosGuia = (
        servicio: ServicioAdicional,
        enOrigen: boolean,
        enDestino: boolean
    ) => {
        servicio.esOrigen = enOrigen;
        servicio.esDestino = enDestino;
        servicio.esSeccionServiciosAdicionales = true;
        servicio.esSeccionOrigenDestino = false;
        servicio.esAbierto = servicio.abierto === 1;
        const categoriaConfig = servicio.configuracion?.conceptoCategoriaSolicitudServicioId;
        if (typeof categoriaConfig === "number" && categoriaConfig > 0) {
            servicio.categoriaId = categoriaConfig;
        }
        servicio.esRetornoDocumentos = servicio.id === enums.servicioAdicional.retornoDocumentos;
        if (servicio.seleccionado === undefined) {
            servicio.seleccionado = false;
        }
        if (servicio.aplicaOrigen === undefined) {
            servicio.aplicaOrigen = false;
        }
        if (servicio.aplicaDestino === undefined) {
            servicio.aplicaDestino = false;
        }
    };

    const obtenerTipoServicioYEnvio = async (
        cliente: ICliente,
        recoleccionOrigen: IRecoleccion,
        recoleccionDestino: IRecoleccion
    ): Promise<{ tipoServicioId: number; tipoEnvioId: number }> => {
        const esOcurreOrigen = recoleccionOrigen.tipoRecoleccionId === enums.tipoRecoleccion.ocurre;
        const esOcurreDestino = recoleccionDestino.tipoRecoleccionId === enums.tipoRecoleccion.ocurre;
        const filter = {
            clienteId: cliente.id,
            areaServicioOrigenId: recoleccionOrigen.areaServicio?.id,
            areaServicioDestinoId: recoleccionDestino.areaServicio?.id,
            esOcurreOrigen,
            esOcurreDestino,
            origenPaisId: recoleccionOrigen.paisId,
            destinoPaisId: recoleccionDestino.paisId,
        };

        const result = await apiSource.obtenerAsync<any>("tipo-envio/by-cliente-ruta", filter);
        const tipoServicioId = result?.tipoServicio?.id ?? 0;
        const tiposEnvio = Array.isArray(result?.tiposEnvio) ? result.tiposEnvio : [];
        const tipoEnvioId = tiposEnvio.length > 0 ? (tiposEnvio[0]?.id ?? 0) : 0;
        return { tipoServicioId, tipoEnvioId };
    };

    const obtenerConveniosServicios = async (
        cliente: ICliente,
        tipoServicioId: number,
        servicios: ServicioAdicional[]
    ) => {
        if (!servicios.length || !tipoServicioId) {
            return [];
        }
        const filtroConvenioServicios = {
            serviciosIds: servicios.map(e => e.id),
            clienteId: cliente.id,
            proyectoId: 0,
            tipoServicioId
        };
        return await apiSource.obtenerAsync<any[]>("/convenio/convenios-servicios", filtroConvenioServicios);
    };

    const aplicarConvenios = (servicios: ServicioAdicional[], conveniosServicios: any[]) => {
        const lista = Array.isArray(conveniosServicios) ? conveniosServicios : [];
        servicios.forEach(s => {
            const convenioId = lista.find(e => e.servicioId === s.id)?.convenioId ?? 0;
            s.Configurar(convenioId);
        });
    };

    const fusionarSeleccionPrevia = (
        catalogo: ServicioAdicional[],
        previos: ServicioAdicional[]
    ): ServicioAdicional[] => {
        if (!previos?.length) {
            return catalogo;
        }
        return catalogo.map(c => {
            const prev = previos.find(p => p.id === c.id);
            if (!prev) {
                return c;
            }
            return new ServicioAdicional({
                ...c,
                seleccionado: prev.seleccionado,
                aplicaOrigen: prev.aplicaOrigen,
                aplicaDestino: prev.aplicaDestino,
                cantidad: prev.cantidad,
                cantidadOrigen: prev.cantidadOrigen,
                cantidadDestino: prev.cantidadDestino,
                costo: prev.costo,
                documentos: prev.documentos,
            });
        });
    };

    const aplicarRetornoDocumentosGuia = (
        servicios: ServicioAdicional[],
        cliente: ICliente,
        serviciosPrevios: ServicioAdicional[] = []
    ): ServicioAdicional[] => {
        const totalDocumentosCliente = cliente.documentos?.length ?? 0;
        return servicios.map(s => {
            if (s.id !== enums.servicioAdicional.retornoDocumentos) {
                return s;
            }
            s.esRetornoDocumentos = true;
            s.esRequerido = totalDocumentosCliente > 0;
            const prev = serviciosPrevios.find(p => p.id === s.id);
            if (totalDocumentosCliente > 0 && !prev?.seleccionado && (s.cantidad == null || s.cantidad === 0)) {
                s.cantidad = totalDocumentosCliente;
            }
            return s;
        });
    };

    return {
        prepararServiciosGuia: async (
            cliente: ICliente,
            recoleccionOrigen: IRecoleccion,
            recoleccionDestino: IRecoleccion,
            serviciosPrevios: ServicioAdicional[] = []
        ): Promise<{ servicios: ServicioAdicional[]; tipoServicioId: number; tipoEnvioId: number }> => {
            let catalogo = combinarServiciosGuia(recoleccionOrigen, recoleccionDestino);
            catalogo = filtrarCobroEnDestinoClienteCredito(catalogo, cliente);
            catalogo = excluirCargosCitaCedisGuiaInHouse(catalogo);
            let tipoServicioId = 0;
            let tipoEnvioId = 0;

            try {
                const tipos = await obtenerTipoServicioYEnvio(cliente, recoleccionOrigen, recoleccionDestino);
                tipoServicioId = tipos.tipoServicioId;
                tipoEnvioId = tipos.tipoEnvioId;
            } catch {
                tipoServicioId = 0;
                tipoEnvioId = 0;
            }

            try {
                const convenios = await obtenerConveniosServicios(cliente, tipoServicioId, catalogo);
                aplicarConvenios(catalogo, convenios);
            } catch {
                aplicarConvenios(catalogo, []);
            }

            let servicios = fusionarSeleccionPrevia(catalogo, serviciosPrevios);
            servicios = aplicarRetornoDocumentosGuia(servicios, cliente, serviciosPrevios);
            servicios = aplicarCargosAutomaticosModalidadGuia(
                servicios,
                recoleccionOrigen,
                recoleccionDestino
            );
            return { servicios, tipoServicioId, tipoEnvioId };
        }
    };
};

export default useGetServiciosGuia;
